/**
 * BranchPanels
 *
 * Agrupa en un solo archivo todos los paneles laterales relacionados
 * con sucursales: visualización (read-only) y creación/edición.
 *
 * Uso en Dashboard:
 *   const branchPanels = useBranchPanels();
 *   ...
 *   branchPanels.openDetail(branch)   // botón "Ver"
 *   branchPanels.openEdit(branch)     // botón "Editar"
 *   branchPanels.openCreate()         // botón "Nueva Sucursal"
 *   ...
 *   <BranchPanels hook={branchPanels} users={users} allCompanies={allCompanies} onSave={loadData} />
 */

import { useState } from "react";
import { type Branch } from "@/services/branchesService";
import { type Company } from "@/services/companiesService";
import { type User } from "@/services/usersService";
import BranchDetailModal from "@/components/BranchDetailModal";
import BranchFormSheet from "@/components/BranchFormSheet";

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useBranchPanels() {
  // Panel de detalle (solo lectura)
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailBranch, setDetailBranch] = useState<Branch | null>(null);

  // Panel de formulario (crear / editar)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formBranch, setFormBranch] = useState<Branch | null>(null);

  const openDetail = (branch: Branch) => {
    setDetailBranch(branch);
    setIsDetailOpen(true);
  };

  const openEdit = (branch: Branch) => {
    setFormBranch(branch);
    setIsFormOpen(true);
  };

  const openCreate = () => {
    setFormBranch(null);
    setIsFormOpen(true);
  };

  return {
    // Estado interno — usado por <BranchPanels />
    _state: { isDetailOpen, detailBranch, isFormOpen, formBranch },
    _setters: { setIsDetailOpen, setIsFormOpen },
    // Handlers públicos — usados por Dashboard
    openDetail,
    openEdit,
    openCreate,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

interface BranchPanelsProps {
  hook: ReturnType<typeof useBranchPanels>;
  /** Lista de usuarios para resolver dueño y managers en el panel de detalle */
  users: User[];
  /** Lista de empresas para el selector en el formulario de creación */
  allCompanies: Company[];
  /** Se llama tras crear / editar / cambiar estado para recargar datos */
  onSave: () => void;
}

export function BranchPanels({
  hook,
  users,
  allCompanies,
  onSave,
}: BranchPanelsProps) {
  const { _state: s, _setters: set } = hook;

  // Derivar company, owner y managers desde la sucursal activa en el detalle
  const detailCompany = s.detailBranch
    ? (allCompanies.find((c) => c.id === s.detailBranch!.company_id) ?? null)
    : null;

  const detailOwner = s.detailBranch
    ? (users.find(
        (u) => u.role_id === 2 && u.company_id === s.detailBranch!.company_id
      ) ?? null)
    : null;

  const detailManagers = s.detailBranch
    ? users.filter(
        (u) => u.role_id === 3 && u.branch_id === s.detailBranch!.id
      )
    : [];

  return (
    <>
      {/* Panel de visualización */}
      <BranchDetailModal
        isOpen={s.isDetailOpen}
        onClose={() => set.setIsDetailOpen(false)}
        branch={s.detailBranch}
        company={detailCompany}
        owner={detailOwner}
        managers={detailManagers}
      />

      {/* Panel de creación / edición */}
      <BranchFormSheet
        isOpen={s.isFormOpen}
        onClose={() => set.setIsFormOpen(false)}
        branch={s.formBranch}
        companies={allCompanies}
        onSave={onSave}
      />
    </>
  );
}
